import { AsyncPipe, DatePipe, LowerCasePipe, NgClass } from '@angular/common';
import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import {
    FormControl,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import { RiderColor } from '@polaris-customerweb/sharedcomponents.core';
import {
    DistanceDisplayComponent,
    DurationDisplayComponent,
    RideTypeService,
} from '@polaris-customerweb/sharedcomponents.shared';
import {
    Geofence,
    PlannedRide,
    TrackedRide,
    Waypoint,
} from '@polaris-customerweb/sharedcomponents.user-content';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { AppState } from 'src/app/models/app-state';
import { GeoFileUploadCollectionCheckbox } from 'src/app/models/geo-file-upload-collection-checkbox';
import { MapDisplay } from 'src/app/models/map-display';
import { GeofenceMapService } from 'src/app/services/geofence-map.service';
import { MapService } from 'src/app/services/map.service';
import { PlannedRideMapService } from 'src/app/services/planned-ride-map.service';
import { TrackedRideMapService } from 'src/app/services/tracked-ride-map.service';
import { WaypointMapService } from 'src/app/services/waypoint-map.service';
import { GeoFileImportService } from 'src/app/state/geo-file-import/geo-file-import.service';
import { GeoFileImportVm } from 'src/app/state/geo-file-import/geo-file-import.vm';
import { AreaDisplayComponent } from '../area-display/area-display.component';
import { ColorPickerComponent } from '../color-picker/color-picker.component';
import { GeoFileImportUploadComponent } from '../geo-file-import-upload/geo-file-import-upload.component';
import { MapComponent } from '../map/map-component/map.component';

@Component({
    imports: [
        AngularSvgIconModule,
        AreaDisplayComponent,
        AsyncPipe,
        ColorPickerComponent,
        DatePipe,
        DistanceDisplayComponent,
        DurationDisplayComponent,
        FormsModule,
        GeoFileImportUploadComponent,
        LowerCasePipe,
        MapComponent,
        MatCheckboxModule,
        MatFormFieldModule,
        MatInputModule,
        MatProgressBarModule,
        MatProgressSpinnerModule,
        MatRadioModule,
        NgClass,
        ReactiveFormsModule,
    ],
    selector: 'rdx-geo-file-import',
    styleUrls: ['./geo-file-import.component.scss'],
    templateUrl: './geo-file-import.component.html',
})
export class GeoFileImportComponent implements OnDestroy {
    @ViewChild('input') input: ElementRef<HTMLInputElement>;
    isLoading = false;
    isShowColorPickerShowing = false;
    vm: GeoFileImportVm;

    nameFormControl = new FormControl('', [Validators.required.bind(this)]);

    constructor(
        configService: ConfigService,
        geofenceMapService: GeofenceMapService,
        geoFileImportService: GeoFileImportService,
        mapService: MapService,
        private matDialogRef: MatDialogRef<GeoFileImportComponent>,
        plannedRideMapService: PlannedRideMapService,
        private matSnackBar: MatSnackBar,
        rideTypeService: RideTypeService,
        private router: Router,
        store: Store<AppState>,
        trackedRideMapService: TrackedRideMapService,
        waypointMapService: WaypointMapService
    ) {
        this.vm = new GeoFileImportVm(
            configService,
            geofenceMapService,
            geoFileImportService,
            mapService,
            plannedRideMapService,
            rideTypeService,
            store,
            trackedRideMapService,
            waypointMapService
        );
    }

    onMapInitialized(mapDisplay: MapDisplay): void {
        this.vm.initMap(mapDisplay);
    }

    onFileSelected(file: File): void {
        this.vm.file = file;
        this.isLoading = true;
        this.vm.uploadFile(this.vm.file).subscribe(() => {
            this.isLoading = false;
        });
    }

    onToggleColorPicker(event: MouseEvent): void {
        event.stopPropagation();
        this.isShowColorPickerShowing = !this.isShowColorPickerShowing;
    }

    onColorChanged(color: RiderColor): void {
        this.isShowColorPickerShowing = false;
        this.vm.itemColor = color;
    }

    onToggleCollectionItemColorPicker(
        item: GeoFileUploadCollectionCheckbox
    ): void {
        this.vm.toggleCollectionItemColorPicker(item);
    }

    onCollectionItemColorChanged(
        color: RiderColor,
        item: GeoFileUploadCollectionCheckbox
    ): void {
        this.vm.setCollectionItemColor(item, color);
    }

    onEnterItem(
        item: PlannedRide | TrackedRide | Geofence | Waypoint | undefined
    ): void {
        if (item) {
            this.vm.highlight(item);
        }
    }

    onSelectAllChanged(): void {
        this.vm.toggleSelectAllItem();
    }

    onImport(): void {
        if (this.vm.isCollection && this.vm.selectedItemCount === 0) {
            return;
        }
        this.vm.importFile().subscribe((res) => {
            if (res) {
                this.onCancel();
                void this.router.navigate(this.vm.getImportedItemUrl(res));
            }
        });
    }

    onEditItem(item: GeoFileUploadCollectionCheckbox): void {
        this.vm.editItem(item);
        setTimeout(() => {
            this.input.nativeElement.focus();
        });
    }

    onStopEdit(item: GeoFileUploadCollectionCheckbox): void {
        this.vm.stopEditItem(item);
    }

    onLeaveItem(): void {
        this.vm.unhighlight();
    }

    onCancel(): void {
        this.matDialogRef.close();
    }

    ngOnDestroy(): void {
        if (this.vm.processing) {
            this.matSnackBar.open(
                $localize`The import will continue in the background`
            );
        }
        this.vm.destroy();
    }
}
